import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon.jsx";
import { Spinner } from "../components/ui.jsx";
import { useDocumentTitle, useLocalePath } from "../i18n/hooks.js";
import { AuthShell } from "../layouts.jsx";
import { useSession } from "../session.jsx";

const POLL_MS = 2000;
const MAX_POLLS = 20;

const isActive = (t) => Boolean(t && t.plan && !["subscribe", "accept_terms"].includes(t.nextVerificationStep));

/** Return page after hosted checkout: /:locale/billing?status=success|cancel */
export default function BillingReturn() {
  useDocumentTitle("billing.title");
  const lp = useLocalePath();
  const [params] = useSearchParams();
  const status = params.get("status") === "success" ? "success" : "cancel";
  const { tenant, refreshTenant } = useSession();
  const [phase, setPhase] = useState(status === "success" ? (isActive(tenant) ? "active" : "waiting") : "cancel");

  // The webhook activates the subscription asynchronously; poll the tenant for a bit.
  useEffect(() => {
    if (phase !== "waiting") return;
    let n = 0;
    let alive = true;
    const tick = async () => {
      n += 1;
      const t = await refreshTenant();
      if (!alive) return;
      if (isActive(t)) setPhase("active");
      else if (n >= MAX_POLLS) setPhase("slow");
      else timer = setTimeout(tick, POLL_MS);
    };
    let timer = setTimeout(tick, 500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [phase, refreshTenant]);

  return (
    <AuthShell>
      <div className="card auth-card center-text">
        {phase === "cancel" ? (
          <>
            <div className="result-icon is-warn">
              <Icon name="x" size={28} />
            </div>
            <h1 className="h2">
              <FormattedMessage id="billing.cancel.title" />
            </h1>
            <p className="muted">
              <FormattedMessage id="billing.cancel.body" />
            </p>
            <div className="btn-row center">
              <Link className="btn btn-primary" to={lp("/onboarding")}>
                <FormattedMessage id="billing.cancel.retry" />
              </Link>
              <Link className="btn btn-ghost" to={lp("/")}>
                <FormattedMessage id="billing.toDashboard" />
              </Link>
            </div>
          </>
        ) : phase === "waiting" ? (
          <>
            <div className="result-icon">
              <Spinner size={28} />
            </div>
            <h1 className="h2">
              <FormattedMessage id="billing.waiting.title" />
            </h1>
            <p className="muted">
              <FormattedMessage id="billing.waiting.body" />
            </p>
          </>
        ) : (
          <>
            <div className="result-icon is-ok">
              <Icon name="check" size={28} strokeWidth={2.4} />
            </div>
            <h1 className="h2">
              <FormattedMessage id={phase === "active" ? "billing.success.title" : "billing.slow.title"} />
            </h1>
            <p className="muted">
              <FormattedMessage id={phase === "active" ? "billing.success.body" : "billing.slow.body"} />
            </p>
            <div className="btn-row center">
              <Link className="btn btn-primary" to={lp("/")}>
                <FormattedMessage id="billing.toDashboard" />
                <Icon name="arrowRight" size={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
